/** @jsxImportSource @emotion/react */

import { useAtom } from 'jotai';
import useNotifications from '../../shared/hooks/use-notifications';
import { getUserChannelData, shouldHideDTDetails } from '../login/v1/utility';
import useAddonPurchase from '../../shared/hooks/use-addon-purchase';
import { entAnalyticsUpgradeModalOpenAtom } from './state';
import { useCallback } from 'react';
import { openPopup } from '../../shared/utils/popup';
import QSModal from '../../shared/components/molecules/modal';
import { css } from '@emotion/react';
import { QSButton, QSIconButton, QSTypography } from '../../shared/components/atoms';
// import { Close, PlayCircle } from "@mui/icons-material";
import ProgressiveImage from '../../shared/components/atoms/progressive-image';
import { PlanAddonTypes } from '../renew-plan/v1/typings';
import ENTAnalyticsPNG from './imgaes/ent-analytics-modal.png';
import { Close } from '@mui/icons-material';
import { ReactComponent as ENTAnalyticsIcon } from './icons/ENTAnalyticsIcon.svg';
import { ReactComponent as ENTFilter } from './icons/ENTFilter.svg';
import { ReactComponent as ENTStar } from './icons/ENTStar.svg';
import { ReactComponent as ENTAnalytics } from './icons/ENTAnalytics.svg';
import Loader from '../../shared/components/molecules/loader';
import PlayCircle from '../../shared/icons/PlayCircle';

const FEATURES = [
  {
    featureLabel: 'Powerful Filters & Export Options',
    desc: 'Monitor key metrics such as average response time, SLA breaches, first response time, and more',
    icon: <ENTFilter />,
  },
  {
    featureLabel: 'Visual Dashboards for Smarter Insights',
    desc: 'Intuitive dashboards with metrics like first response time, outgoing messages, and more',
    icon: <ENTAnalytics />,
  },
  {
    featureLabel: 'AI-Powered Message Prioritization',
    desc: 'Automatically detect and filter negative or urgent messages using AI.',
    icon: <ENTStar />,
  },
];

export default function EnterpriseAnalyticsUpgradeModal() {
  const [upgradeModal, setUpgradeModal] = useAtom(entAnalyticsUpgradeModalOpenAtom);
  const { addNotification } = useNotifications();
  const channelData = getUserChannelData();
  const ytVideoLink = 'https://www.youtube.com/watch?v=fIvl64JlxoY';
  const hideDTDetails = shouldHideDTDetails();
  const { purchaseAddon, loading: purchaseLoading } = useAddonPurchase();
  let contactUsLink = `https://wa.me/${channelData?.supportNumber}?text=I%27m%20interested%20in%20buying%20WooCommerce%20Addon`;

  const onClose = useCallback(() => {
    setUpgradeModal(false);
  }, [setUpgradeModal]);

  const onClickWatchDemo = useCallback(async () => {
    try {
      openPopup(ytVideoLink, '_blank');
      onClose();
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'An error occurred.',
        type: 'error',
      });
    }
  }, [addNotification, onClose]);

  const onClickContactUs = useCallback(async () => {
    try {
      if (hideDTDetails) {
        openPopup(contactUsLink, '_blank');
      } else {
        await purchaseAddon(PlanAddonTypes.EnterpriseAnalytics);
      }
      onClose();
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'An error occurred. Please try again',
        type: 'error',
      });
    }
  }, [addNotification, onClose]);

  const orgName = channelData?.name ?? 'DoubleTick';

  if (!upgradeModal) {
    return null;
  }

  return (
    <QSModal
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
      `}
      open={upgradeModal}
      onClose={onClose}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          border-radius: 8px;
          overflow: hidden;
          background: white;
          outline: none;
          position: relative;
          padding: 16px;
          max-width: 730px;
        `}
      >
        <QSIconButton
          css={css`
            position: absolute;
            top: 8px;
            right: 8px;
          `}
          sx={{}}
          onClick={onClose}
        >
          <Close />
        </QSIconButton>
        <div
          css={css`
            flex: 1;
            overflow: auto;
            display: flex;
            align-items: flex-start;
            flex-wrap: wrap;
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex: 1;
              background-color: #f6f1e7;
              border-radius: 8px;
              overflow: hidden;
              width: 300px;
            `}
          >
            <ProgressiveImage imgProps={{ width: '100%', height: '390px' }} src={ENTAnalyticsPNG} />
          </div>
          <div
            css={css`
              flex: 1;
              padding: 0 12px;
              padding-left: 20px;
              max-width: 350px;
            `}
          >
            <span
              css={css`
                border: 1px solid #e0e0e9;
                border-radius: 50px;
                padding: 4px 8px 4px 4px;
                display: inline-flex;
                align-items: center;
                font-size: 12px;
                margin-top: 0;
                font-weight: 500;
                color: #596bd7;
                background-color: #596bd714;
              `}
            >
              <ENTAnalyticsIcon />
              <span
                css={css`
                  margin-left: 5px;
                `}
              >
                Enterprise Grade AI-Powered Analytics
              </span>
            </span>
            <QSTypography
              css={css`
                font-weight: 600;
                padding-right: 12px;
                font-size: 20px;
                overflow: hidden;
                line-height: 28px;
                margin-bottom: 16px;
                margin-top: 16px;
              `}
            >
              Deep, data-driven insights to analyse performance of each WhatsApp Business Account
            </QSTypography>

            {FEATURES.map((feature, key) => (
              <div
                key={key}
                css={css`
                  display: inline-flex;
                  align-items: center;
                  justify-content: flex-start;
                  margin-bottom: 10px;
                  font-weight: 500;
                  gap: 12px;
                `}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {feature?.icon}
                </div>
                <span>
                  <div
                    css={css`
                      font-family: Inter;
                      font-size: 14px;
                      font-weight: 500;
                      line-height: 16.94px;
                      text-align: left;
                      color: rgba(45, 45, 45, 1);
                    `}
                  >
                    {feature?.featureLabel}
                  </div>
                  <div
                    css={css`
                      font-family: Inter;
                      font-size: 12px;
                      font-weight: 400;
                      line-height: 14.52px;
                      color: rgba(122, 122, 122, 1);
                    `}
                  >
                    {feature?.desc}
                  </div>
                </span>
              </div>
            ))}
            <div
              css={css`
                margin-top: 24px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 8px;
              `}
            >
              <QSButton
                css={css`
                  height: 40px;
                  width: 100%;
                  white-space: nowrap;
                `}
                onClick={onClickContactUs}
              >
                {hideDTDetails ? (
                  'Contact Us'
                ) : (
                  <>
                    {purchaseLoading ? (
                      <Loader
                        size={16}
                        loaderProps={{
                          sx: {
                            color: 'white',
                          },
                        }}
                      />
                    ) : (
                      'Purchase Now'
                    )}
                  </>
                )}
              </QSButton>
            </div>
          </div>
        </div>
      </div>
    </QSModal>
  );
}
